import store from "../../../../../store";
import { AutomationEventTypes } from "../AutomationModuleTypes";
import _ from "lodash";

const vt = {
    STRING: "string",
    NUMBER: "number",
    BOOLEAN: "boolean",
    DATE: "date",
    DATETIME: "datetime",
    TIME: "time",
    ENUM: "enum",
    ARRAY: "array",
    ARRAY_NUMBER: "array_number",
    ARRAY_STRING: "array_string",
    ARRAY_DATE: "array_date",
    ARRAY_OBJECT: "array_objectId",
    ARRAY_BOOLEAN: "array_boolean",
    OBJECT: "object",
    MIXED: "mixed",
};
const contexts = {
    CONTACT: "contact",
    CONVERSATION: "conversation",
    BLOCK: "block",
    EVENT: "event",
    WEBHOOK: "webhook",
    SYSTEM: "system",
    CUSTOM: "custom",
};
const defaultOperators = {
    string: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
    ],
    array: ["equal", "not_equal", "contains", "not_contains", "empty", "not_empty"],
    number: ["equal", "not_equal", "greater_than", "less_than", "greater_than_or_equal", "less_than_or_equal"],
    boolean: ["equal", "not_equal", "empty", "not_empty"],
    date: [
        "equal",
        "not_equal",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
        "empty",
        "not_empty",
    ],
    ObjectId: ["equal", "not_equal", "empty", "not_empty"],
    object: ["equal", "not_equal", "empty", "not_empty"],
    mixed: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
    ],
};

const ga = (
    context,
    value,
    label,
    valueType = "string",
    controlType = "input",
    subAttributes = [],
    defaultFilterValue = undefined,
    defaultOperator = undefined,
    availableOperators = undefined,
    options = undefined
) => {
    return {
        context,
        name,
        value,
        label,
        defaultOperator: defaultOperator || "equal",
        defaultFilterValue,
        availableOperators: availableOperators || defaultOperators[valueType] || ["equal", "not_equal"],
        controlType,
        options,
        subAttributes,
        valueType,
    };
};

class TableModuleAttributeFactory {
    /**
     *
     * @param parent {AutomationAttributesGenerator}
     */
    constructor(parent) {
        this.parent = parent;
    }

    generateTableAttributes(prefix) {
        const context = contexts.EVENT;
        return [
            ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
            ga(context, `${prefix}._id`, "Tablo ID", vt.STRING),
            ga(context, `${prefix}.name`, "Tablo Adı", vt.STRING),
            ga(context, `${prefix}.description`, "Tablo Açıklaması", vt.STRING),
        ];
    }

    generateTableFieldAttributes(prefix) {
        const context = contexts.EVENT;
        return [
            ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
            ga(context, `${prefix}._id`, "Field ID", vt.STRING),
            ga(context, `${prefix}.name`, "Field Name", vt.STRING),
            ga(context, `${prefix}.fieldType`, "Field Control Type", vt.STRING),
            ga(context, `${prefix}.valueType`, "Field Value Type", vt.STRING),
        ];
    }

    generate() {
        const tableCreatedEvents = this.recordCreated();
        const tableUpdatedEvents = this.recordUpdated();
        const tableDeletedEvents = this.recordDeleted();

        return _.concat(tableCreatedEvents, tableUpdatedEvents, tableDeletedEvents);
    }

    generateRecordAttributes(context, prefix, table, wildcard = true) {
        return [
            wildcard ? ga(context, `${prefix}.$..*`, "*", vt.OBJECT) : false,
            ga(context, `${prefix}._id`, "Record ID", vt.STRING),
            ga(context, `${prefix}.recordId`, "Record Index", vt.NUMBER),
            ga(context, `${prefix}.createdAt`, "Created At", vt.DATETIME),
            ga(context, `${prefix}.updatedAt`, "Updated At", vt.DATETIME),
            ga(context, `${prefix}.fields`, "Fields", vt.OBJECT, undefined, [
                ga(context, `${prefix}.fields.$..*`, "*", vt.OBJECT),
                ...table.fields.map((field) => {
                    const vtMap = {
                        string: vt.STRING,
                        number: vt.NUMBER,
                        boolean: vt.BOOLEAN,
                        date: vt.DATETIME,
                        array: vt.ARRAY_OBJECT,
                        json: vt.OBJECT,
                    };
                    return ga(context, `${prefix}.fields.${field._id}`, field.name, vt.OBJECT, undefined, [
                        ga(
                            context,
                            `${prefix}.fields.${field._id}.field`,
                            "Field Info",
                            vt.OBJECT,
                            undefined,
                            this.generateTableFieldAttributes(`${prefix}.fields.${field._id}.field`)
                        ),
                        ga(
                            context,
                            `${prefix}.fields.${field._id}.value`,
                            "Field Value",
                            vtMap[field.valueType] || vt.MIXED
                        ),
                    ]);
                }),
            ]),
        ].filter(Boolean);
    }

    recordCreated() {
        const tables = store.state.table.tables;
        const e = AutomationEventTypes.TABLE.RECORD_CREATED;
        const ga = this.parent.generateAttribute;

        const result = tables.map((table) => {
            const prefix = `event.${e}.${table._id}`;
            return [
                ga(contexts.EVENT, `${prefix}`, "Created Record on " + table.name, vt.OBJECT, undefined, [
                    ga(contexts.EVENT, `${prefix}.$..*`, "*", vt.OBJECT),
                    ga(contexts.EVENT, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                        ...this.generateTableAttributes(`${prefix}.table`),
                    ]),
                    ga(contexts.EVENT, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                        ga(contexts.EVENT, `${prefix}.record.$..*`, "*", vt.OBJECT),
                        ga(contexts.EVENT, `${prefix}.record._id`, "Record ID", vt.STRING),
                        ga(contexts.EVENT, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                        ga(contexts.EVENT, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                            ga(contexts.EVENT, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                            ...table.fields.map((field) => {
                                const vtMap = {
                                    string: vt.STRING,
                                    number: vt.NUMBER,
                                    boolean: vt.BOOLEAN,
                                    date: vt.DATETIME,
                                    array: vt.ARRAY_OBJECT,
                                    json: vt.OBJECT,
                                };
                                return ga(
                                    contexts.EVENT,
                                    `${prefix}.record.fields.${field._id}`,
                                    field.name,
                                    vt.OBJECT,
                                    undefined,
                                    [
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.field`,
                                            "Field Info",
                                            vt.OBJECT,
                                            undefined,
                                            this.generateTableFieldAttributes(
                                                `${prefix}.record.fields.${field._id}.field`
                                            )
                                        ),
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.value`,
                                            "Field Value",
                                            vtMap[field.valueType] || vt.MIXED
                                        ),
                                    ]
                                );
                            }),
                        ]),
                    ]),
                ]),
            ];
        });

        return _.flatten(result);
    }

    recordUpdated() {
        const tables = store.state.table.tables;
        const e = AutomationEventTypes.TABLE.RECORD_UPDATED;
        const ga = this.parent.generateAttribute;

        const result = tables.map((table) => {
            const prefix = `event.${e}.${table._id}`;
            return [
                ga(contexts.EVENT, `${prefix}`, "Updated Record on " + table.name, vt.OBJECT, undefined, [
                    ga(contexts.EVENT, `${prefix}.$..*`, "*", vt.OBJECT),
                    ga(contexts.EVENT, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                        ...this.generateTableAttributes(`${prefix}.table`),
                    ]),
                    ga(contexts.EVENT, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                        ga(contexts.EVENT, `${prefix}.record.$..*`, "*", vt.OBJECT),
                        ga(contexts.EVENT, `${prefix}.record._id`, "Record ID", vt.STRING),
                        ga(contexts.EVENT, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                        ga(contexts.EVENT, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                            ga(contexts.EVENT, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                            ...table.fields.map((field) => {
                                const vtMap = {
                                    string: vt.STRING,
                                    number: vt.NUMBER,
                                    boolean: vt.BOOLEAN,
                                    date: vt.DATETIME,
                                    array: vt.ARRAY_OBJECT,
                                    json: vt.OBJECT,
                                };
                                return ga(
                                    contexts.EVENT,
                                    `${prefix}.record.fields.${field._id}`,
                                    field.name,
                                    vt.OBJECT,
                                    undefined,
                                    [
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.field`,
                                            "Field Info",
                                            vt.OBJECT,
                                            undefined,
                                            this.generateTableFieldAttributes(
                                                `${prefix}.record.fields.${field._id}.field`
                                            )
                                        ),
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.value`,
                                            "Field Value",
                                            vtMap[field.valueType] || vt.MIXED
                                        ),
                                    ]
                                );
                            }),
                        ]),
                    ]),
                    ga(contexts.EVENT, `${prefix}.updatedField`, "Updated Field", vt.OBJECT, undefined, [
                        ...this.generateTableFieldAttributes(`${prefix}.updatedField`),
                        ga(contexts.EVENT, `${prefix}.updatedField.oldValue`, "Old Value", vt.MIXED),
                        ga(contexts.EVENT, `${prefix}.updatedField.newValue`, "New Value", vt.MIXED),
                    ]),
                ]),
            ];
        });

        return _.flatten(result);
    }

    recordDeleted() {
        const tables = store.state.table.tables;
        const e = AutomationEventTypes.TABLE.RECORD_DELETED;
        const ga = this.parent.generateAttribute;

        const result = tables.map((table) => {
            const prefix = `event.${e}.${table._id}`;
            return [
                ga(contexts.EVENT, `${prefix}`, "Deleted Record on " + table.name, vt.OBJECT, undefined, [
                    ga(contexts.EVENT, `${prefix}.$..*`, "*", vt.OBJECT),
                    ga(contexts.EVENT, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                        ...this.generateTableAttributes(`${prefix}.table`),
                    ]),
                    ga(contexts.EVENT, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                        ga(contexts.EVENT, `${prefix}.record.$..*`, "*", vt.OBJECT),
                        ga(contexts.EVENT, `${prefix}.record._id`, "Record ID", vt.STRING),
                        ga(contexts.EVENT, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                        ga(contexts.EVENT, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                            ga(contexts.EVENT, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                            ...table.fields.map((field) => {
                                const vtMap = {
                                    string: vt.STRING,
                                    number: vt.NUMBER,
                                    boolean: vt.BOOLEAN,
                                    date: vt.DATETIME,
                                    array: vt.ARRAY_OBJECT,
                                    json: vt.OBJECT,
                                };
                                return ga(
                                    contexts.EVENT,
                                    `${prefix}.record.fields.${field._id}`,
                                    field.name,
                                    vt.OBJECT,
                                    undefined,
                                    [
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.field`,
                                            "Field Info",
                                            vt.OBJECT,
                                            undefined,
                                            this.generateTableFieldAttributes(
                                                `${prefix}.record.fields.${field._id}.field`
                                            )
                                        ),
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.value`,
                                            "Field Value",
                                            vtMap[field.valueType] || vt.MIXED
                                        ),
                                    ]
                                );
                            }),
                        ]),
                    ]),
                ]),
            ];
        });

        return _.flatten(result);
    }

    listBlockFindRecordsFields(prefix, block) {
        const tableId = block.payload?.tableId;
        if (!tableId) return [];
        else {
            const table = store.state.table.tables.find((t) => t._id === tableId);
            if (!table) return [];
            // prefix = `${prefix}.${table._id}`;
            prefix = `${prefix}`;

            // minimizedDataFormat kontrolü
            if (block.payload && block.payload.minimizedDataFormat) {
                return [
                    ga(contexts.BLOCK, `${prefix}.$..*`, "*", vt.OBJECT),
                    ga(contexts.BLOCK, `${prefix}.total`, "Total Records", vt.NUMBER),
                    ga(contexts.BLOCK, `${prefix}.items`, "items", vt.ARRAY_OBJECT, undefined, [
                        ga(contexts.BLOCK, `${prefix}.items.$..*`, "*", vt.ARRAY_OBJECT),
                        ga(contexts.BLOCK, `${prefix}.items.$.._id`, "Record ID", vt.STRING),
                        ga(contexts.BLOCK, `${prefix}.items.$..recordNo`, "Record Number", vt.NUMBER),
                        ga(contexts.BLOCK, `${prefix}.items.$..createdAt`, "Created At", vt.DATETIME),
                        ga(contexts.BLOCK, `${prefix}.items.$..updatedAt`, "Updated At", vt.DATETIME),
                        ga(contexts.BLOCK, `${prefix}.items.$..tableId`, "Table ID", vt.STRING),
                        ga(contexts.BLOCK, `${prefix}.items.$..tableName`, "Table Name", vt.STRING),
                        // apiName'e göre alanları ekle
                        ...table.fields
                            .filter(field => field.apiName)
                            .map(field => {
                                const vtMap = {
                                    string: vt.STRING,
                                    number: vt.NUMBER,
                                    boolean: vt.BOOLEAN,
                                    date: vt.DATETIME,
                                    array: vt.ARRAY_OBJECT,
                                    json: vt.OBJECT,
                                };
                                return ga(
                                    contexts.BLOCK,
                                    `${prefix}.items.$..${field.apiName}`,
                                    field.name,
                                    vtMap[field.valueType] || vt.MIXED
                                );
                            })
                    ])
                ];
            }

            // Standart format (mevcut davranış)
            return [
                ga(contexts.BLOCK, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(contexts.BLOCK, `${prefix}.count`, "count", vt.NUMBER),
                ga(contexts.BLOCK, `${prefix}.items`, "items", vt.ARRAY_OBJECT, undefined, [
                    ga(contexts.BLOCK, `${prefix}.items.$..*`, "*", vt.ARRAY_OBJECT),
                    ga(contexts.BLOCK, `${prefix}.items.$..record`, "Record", vt.ARRAY_OBJECT, undefined, [
                        ...this.generateRecordAttributes(contexts.BLOCK, `${prefix}.items.$..record`, table, false),
                    ]),
                    ga(contexts.BLOCK, `${prefix}.items.$..table`, "Table", vt.ARRAY_OBJECT, undefined, [
                        ...this.generateTableAttributes(contexts.BLOCK, `${prefix}.items.$..table`),
                    ]),
                ]),
            ];
        }
    }

    listBlockFindRecordFields(prefix, block, wildcard = true) {
        const tableId = block.payload?.tableId;
        if (!tableId) return [];
        else {
            const table = store.state.table.tables.find((t) => t._id === tableId);
            if (!table) return [];
            // prefix = `${prefix}.${table._id}`;
            prefix = `${prefix}`;

            // minimizedDataFormat kontrolü
            if (block.payload && block.payload.minimizedDataFormat) {
                return [
                    wildcard ? ga(contexts.BLOCK, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT) : false,
                    ga(contexts.BLOCK, `${prefix}._id`, "Record ID", vt.STRING),
                    ga(contexts.BLOCK, `${prefix}.recordNo`, "Record Number", vt.NUMBER),
                    ga(contexts.BLOCK, `${prefix}.createdAt`, "Created At", vt.DATETIME),
                    ga(contexts.BLOCK, `${prefix}.updatedAt`, "Updated At", vt.DATETIME),
                    ga(contexts.BLOCK, `${prefix}.tableId`, "Table ID", vt.STRING),
                    ga(contexts.BLOCK, `${prefix}.tableName`, "Table Name", vt.STRING),
                    // apiName'e göre alanları ekle
                    ...table.fields
                        .filter(field => field.apiName)
                        .map(field => {
                            const vtMap = {
                                string: vt.STRING,
                                number: vt.NUMBER,
                                boolean: vt.BOOLEAN,
                                date: vt.DATETIME,
                                array: vt.ARRAY_OBJECT,
                                json: vt.OBJECT,
                            };
                            return ga(
                                contexts.BLOCK,
                                `${prefix}.${field.apiName}`,
                                field.name,
                                vtMap[field.valueType] || vt.MIXED
                            );
                        })
                ].filter(Boolean);
            }

            // Standart format (mevcut davranış)
            return [
                wildcard ? ga(contexts.BLOCK, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT) : false,
                ga(contexts.BLOCK, `${prefix}.record`, "Record", vt.ARRAY_OBJECT, undefined, [
                    ...this.generateRecordAttributes(contexts.BLOCK, `${prefix}.record`, table, false),
                ]),
                ga(contexts.BLOCK, `${prefix}.table`, "Table", vt.ARRAY_OBJECT, undefined, [
                    ...this.generateTableAttributes(contexts.BLOCK, `${prefix}.table`),
                ]),
            ].filter(Boolean);
        }
    }

    buttonClicked(tableId) {
        const tables = store.state.table.tables.filter((t) => t._id === tableId);
        const e = AutomationEventTypes.TABLE.RECORD_AUTOMATION_BUTTON_CLICKED;
        const ga = this.parent.generateAttribute;

        const result = tables.map((table) => {
            const prefix = `event.${e}.${table._id}`;
            return [
                ga(contexts.EVENT, `${prefix}`, "Clicked Button on " + table.name, vt.OBJECT, undefined, [
                    ga(contexts.EVENT, `${prefix}.$..*`, "*", vt.OBJECT),
                    ga(contexts.EVENT, `${prefix}.userId`, "Clicked User ID", vt.STRING),
                    ga(contexts.EVENT, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                        ...this.generateTableAttributes(`${prefix}.table`),
                    ]),
                    ga(contexts.EVENT, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                        ga(contexts.EVENT, `${prefix}.record.$..*`, "*", vt.OBJECT),
                        ga(contexts.EVENT, `${prefix}.record._id`, "Record ID", vt.STRING),
                        ga(contexts.EVENT, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                        ga(contexts.EVENT, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                        ga(contexts.EVENT, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                            ga(contexts.EVENT, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                            ...table.fields.map((field) => {
                                const vtMap = {
                                    string: vt.STRING,
                                    number: vt.NUMBER,
                                    boolean: vt.BOOLEAN,
                                    date: vt.DATETIME,
                                    array: vt.ARRAY_OBJECT,
                                    json: vt.OBJECT,
                                };
                                return ga(
                                    contexts.EVENT,
                                    `${prefix}.record.fields.${field._id}`,
                                    field.name,
                                    vt.OBJECT,
                                    undefined,
                                    [
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.field`,
                                            "Field Info",
                                            vt.OBJECT,
                                            undefined,
                                            this.generateTableFieldAttributes(
                                                `${prefix}.record.fields.${field._id}.field`
                                            )
                                        ),
                                        ga(
                                            contexts.EVENT,
                                            `${prefix}.record.fields.${field._id}.value`,
                                            "Field Value",
                                            vtMap[field.valueType] || vt.MIXED
                                        ),
                                    ]
                                );
                            }),
                        ]),
                    ]),
                    ga(contexts.EVENT, `${prefix}.field`, "Button Field", vt.OBJECT, undefined, [
                        ...this.generateTableFieldAttributes(`${prefix}.field`),
                    ]),
                ]),
            ];
        });

        return _.flatten(result);
    }

    listAttributesForChatbot(blocks) {
        console.log("listAttributesForChatbot", blocks);
        const context = "EVENT";
        const attributes = blocks.map((block) => {
            if (block.type === "TABLE_CREATE_RECORD") {
                const prefix = `block.${block.id}`;
                const table = store.state.table.tables.find((t) => t._id === block.payload.tableId);
                if (!table) {
                    console.error("Table not found on this block");
                    return [];
                }
                const blockName = block.name || block.type;
                return [
                    ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                        ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                        ga(context, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                            ...this.generateTableAttributes(`${prefix}.table`),
                        ]),
                        ga(context, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                            ga(context, `${prefix}.record.$..*`, "*", vt.OBJECT),
                            ga(context, `${prefix}.record._id`, "Record ID", vt.STRING),
                            ga(context, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                            ga(context, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                            ga(context, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                            ga(context, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                                ga(context, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                                ...table.fields.map((field) => {
                                    const vtMap = {
                                        string: vt.STRING,
                                        number: vt.NUMBER,
                                        boolean: vt.BOOLEAN,
                                        date: vt.DATETIME,
                                        array: vt.ARRAY_OBJECT,
                                        json: vt.OBJECT,
                                    };
                                    return ga(
                                        context,
                                        `${prefix}.record.fields.${field._id}`,
                                        field.name,
                                        vt.OBJECT,
                                        undefined,
                                        [
                                            ga(
                                                context,
                                                `${prefix}.record.fields.${field._id}.field`,
                                                "Field Info",
                                                vt.OBJECT,
                                                undefined,
                                                this.generateTableFieldAttributes(
                                                    `${prefix}.record.fields.${field._id}.field`
                                                )
                                            ),
                                            ga(
                                                context,
                                                `${prefix}.record.fields.${field._id}.value`,
                                                "Field Value",
                                                vtMap[field.valueType] || vt.MIXED
                                            ),
                                        ]
                                    );
                                }),
                            ]),
                        ]),
                    ]),
                ];
            } else if (block.type === "TABLE_DELETE_RECORD") {
                const prefix = `block.${block.id}`;
                const table = store.state.table.tables.find((t) => t._id === block.payload.tableId);
                if (!table) {
                    console.error("Table not found on this block");
                    return [];
                }
                const blockName = block.name || block.type;
                return [
                    ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                        ga(context, `${prefix}.success`, "Is Success", vt.BOOLEAN),
                    ]),
                ];
            } else if (block.type === "TABLE_UPDATE_RECORD") {
                const prefix = `block.${block.id}`;
                const table = store.state.table.tables.find((t) => t._id === block.payload.tableId);
                if (!table) {
                    console.error("Table not found on this block");
                    return [];
                }
                const blockName = block.name || block.type;
                return [
                    ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                        ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                        ga(context, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                            ...this.generateTableAttributes(`${prefix}.table`),
                        ]),
                        ga(context, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                            ga(context, `${prefix}.record.$..*`, "*", vt.OBJECT),
                            ga(context, `${prefix}.record._id`, "Record ID", vt.STRING),
                            ga(context, `${prefix}.record.recordId`, "Record Index", vt.NUMBER),
                            ga(context, `${prefix}.record.createdAt`, "Created At", vt.DATETIME),
                            ga(context, `${prefix}.record.updatedAt`, "Updated At", vt.DATETIME),
                            ga(context, `${prefix}.record.fields`, "Fields", vt.OBJECT, undefined, [
                                ga(context, `${prefix}.record.fields.$..*`, "*", vt.OBJECT),
                                ...table.fields.map((field) => {
                                    const vtMap = {
                                        string: vt.STRING,
                                        number: vt.NUMBER,
                                        boolean: vt.BOOLEAN,
                                        date: vt.DATETIME,
                                        array: vt.ARRAY_OBJECT,
                                        json: vt.OBJECT,
                                    };
                                    return ga(
                                        context,
                                        `${prefix}.record.fields.${field._id}`,
                                        field.name,
                                        vt.OBJECT,
                                        undefined,
                                        [
                                            ga(
                                                context,
                                                `${prefix}.record.fields.${field._id}.field`,
                                                "Field Info",
                                                vt.OBJECT,
                                                undefined,
                                                this.generateTableFieldAttributes(
                                                    `${prefix}.record.fields.${field._id}.field`
                                                )
                                            ),
                                            ga(
                                                context,
                                                `${prefix}.record.fields.${field._id}.value`,
                                                "Field Value",
                                                vtMap[field.valueType] || vt.MIXED
                                            ),
                                        ]
                                    );
                                }),
                            ]),
                        ]),
                    ]),
                ];
            } else if (block.type === "TABLE_FIND_RECORD") {
                const prefix = `block.${block.id}`;
                const table = store.state.table.tables.find((t) => t._id === block.payload.tableId);
                if (!table) {
                    console.error("Table not found on this block");
                    return [];
                }
                const blockName = block.name || block.type;
                
                // minimizedDataFormat kontrolü
                if (block.payload && block.payload.minimizedDataFormat) {
                    // minimizedDataFormat aktif ise basitleştirilmiş format için attribute listesi
                    return [
                        ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                            ga(context, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT),
                            ga(context, `${prefix}._id`, "Record ID", vt.STRING),
                            ga(context, `${prefix}.recordNo`, "Record Number", vt.NUMBER),
                            ga(context, `${prefix}.createdAt`, "Created At", vt.DATETIME),
                            ga(context, `${prefix}.updatedAt`, "Updated At", vt.DATETIME),
                            ga(context, `${prefix}.tableId`, "Table ID", vt.STRING),
                            ga(context, `${prefix}.tableName`, "Table Name", vt.STRING),
                            // apiName'e göre alanları ekle
                            ...table.fields
                                .filter(field => field.apiName)
                                .map(field => {
                                    const vtMap = {
                                        string: vt.STRING,
                                        number: vt.NUMBER,
                                        boolean: vt.BOOLEAN,
                                        date: vt.DATETIME,
                                        array: vt.ARRAY_OBJECT,
                                        json: vt.OBJECT,
                                    };
                                    return ga(
                                        context,
                                        `${prefix}.${field.apiName}`,
                                        field.name,
                                        vtMap[field.valueType] || vt.MIXED
                                    );
                                })
                        ]),
                    ];
                }
                
                // Standart format (mevcut davranış)
                return [
                    ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                        ga(context, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT),
                        //table
                        ga(context, `${prefix}.table`, "Table", vt.OBJECT, undefined, [
                            ...this.generateTableAttributes(`${prefix}.table`),
                        ]),
                        //record
                        ga(context, `${prefix}.record`, "Record", vt.OBJECT, undefined, [
                            ...this.generateRecordAttributes(context, `${prefix}.record`, table, false),
                        ]),
                    ]),
                ];
            } else if (block.type === "TABLE_FIND_RECORDS") {
                const prefix = `block.${block.id}`;
                const table = store.state.table.tables.find((t) => t._id === block.payload.tableId);
                if (!table) {
                    console.error("Table not found on this block");
                    return [];
                }
                const blockName = block.name || block.type;
                
                // minimizedDataFormat kontrolü
                if (block.payload && block.payload.minimizedDataFormat) {
                    // minimizedDataFormat aktif ise basitleştirilmiş format için attribute listesi
                    return [
                        ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                            ga(context, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT),
                            ga(context, `${prefix}.total`, "Total Records", vt.NUMBER),
                            ga(context, `${prefix}.items`, "Items", vt.ARRAY_OBJECT, undefined, [
                                ga(context, `${prefix}.items.$..*`, "*", vt.ARRAY_OBJECT),
                                ga(context, `${prefix}.items.$.._id`, "Record ID", vt.STRING),
                                ga(context, `${prefix}.items.$..recordNo`, "Record Number", vt.NUMBER),
                                ga(context, `${prefix}.items.$..createdAt`, "Created At", vt.DATETIME),
                                ga(context, `${prefix}.items.$..updatedAt`, "Updated At", vt.DATETIME),
                                ga(context, `${prefix}.items.$..tableId`, "Table ID", vt.STRING),
                                ga(context, `${prefix}.items.$..tableName`, "Table Name", vt.STRING),
                                // apiName'e göre alanları ekle
                                ...table.fields
                                    .filter(field => field.apiName)
                                    .map(field => {
                                        const vtMap = {
                                            string: vt.STRING,
                                            number: vt.NUMBER,
                                            boolean: vt.BOOLEAN,
                                            date: vt.DATETIME,
                                            array: vt.ARRAY_OBJECT,
                                            json: vt.OBJECT,
                                        };
                                        return ga(
                                            context,
                                            `${prefix}.items.$..${field.apiName}`,
                                            field.name,
                                            vtMap[field.valueType] || vt.MIXED
                                        );
                                    })
                            ]),
                        ]),
                    ];
                }
                
                // Standart format (mevcut davranış)
                return [
                    ga(context, `${prefix}`, "Block  " + blockName, vt.OBJECT, undefined, [
                        ga(context, `${prefix}.$..*`, "*", vt.ARRAY_OBJECT),

                        ga(context, `${prefix}.total`, "Total", vt.NUMBER),
                        ga(context, `${prefix}.items`, "Items", vt.ARRAY_OBJECT, undefined, [
                            ga(context, `${prefix}.items.$..*`, "*", vt.ARRAY_OBJECT),
                            ga(context, `${prefix}.items.$..table`, "Table", vt.OBJECT, undefined, [
                                ...this.generateTableAttributes(`${prefix}.items.$..table`),
                            ]),
                            ga(context, `${prefix}.items.$..record`, "Record", vt.OBJECT, undefined, [
                                ...this.generateRecordAttributes(context, `${prefix}.items.$..record`, table, false),
                            ]),
                        ]),
                    ]),
                ];
            }
            return [];
        });
        const result = _.flatten(attributes);
        console.log("listAttributesForChatbot after", { blocks, result });
        return result;
    }
}

export default TableModuleAttributeFactory;
